import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// In-app confirm/prompt to replace window.confirm/window.prompt — the native
// dialogs render inconsistently across browsers/devices and can't be styled.
// Imperative, promise-based API via context: const dlg = useDialog();
//   await dlg.confirm({ title, message, confirmLabel, cancelLabel, danger })  -> boolean
//   await dlg.prompt({ title, message, placeholder, defaultValue })           -> string | null

const DialogCtx = createContext(null);
export function useDialog() { return useContext(DialogCtx); }

export function DialogProvider({ children }) {
  const [state, setState] = useState(null);
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    setState({ kind: 'confirm', confirmLabel: 'אישור', cancelLabel: 'ביטול', ...opts, resolve });
  }), []);

  const prompt = useCallback((opts = {}) => new Promise((resolve) => {
    setInput(opts.defaultValue || '');
    setState({ kind: 'prompt', confirmLabel: 'הוספה', cancelLabel: 'ביטול', ...opts, resolve });
  }), []);

  const close = useCallback((value) => {
    setState((s) => { s?.resolve(value); return null; });
    setInput('');
  }, []);

  const cancelValue = state?.kind === 'prompt' ? null : false;
  const acceptValue = state?.kind === 'prompt' ? (input.trim() || null) : true;

  // Esc closes; focus the input when a prompt opens.
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(cancelValue); };
    document.addEventListener('keydown', onKey);
    if (state.kind === 'prompt') setTimeout(() => inputRef.current?.focus(), 30);
    return () => document.removeEventListener('keydown', onKey);
  }, [state, close, cancelValue]);

  return (
    <DialogCtx.Provider value={{ confirm, prompt }}>
      {children}
      {state && (
        <div className="rd-modal-overlay" onClick={() => close(cancelValue)}>
          <div className="rd-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {state.title && <h3 className="rd-modal-title">{state.title}</h3>}
            {state.message && <p className="rd-modal-msg">{state.message}</p>}
            {state.kind === 'prompt' && (
              <input
                ref={inputRef}
                className="rd-modal-input"
                value={input}
                placeholder={state.placeholder || ''}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') close(input.trim() || null); }}
              />
            )}
            <div className="rd-modal-actions">
              <button type="button" className="rd-modal-btn ghost" onClick={() => close(cancelValue)}>
                {state.cancelLabel}
              </button>
              <button
                type="button"
                className={`rd-modal-btn primary ${state.danger ? 'danger' : ''}`}
                onClick={() => close(acceptValue)}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogCtx.Provider>
  );
}
